import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type MenuEntry =
  | {
      type?: 'item';
      id: string;
      label: string;
      icon?: LucideIcon;
      /** Mono hint (shortcut) on the right. */
      shortcut?: string;
      disabled?: boolean;
      /** Brand-tinted destructive item. Never bind to an unconfirmed shortcut. */
      destructive?: boolean;
      onSelect: () => void;
    }
  | { type: 'checkbox'; id: string; label: string; checked: boolean; disabled?: boolean; onCheckedChange: (checked: boolean) => void }
  | { type: 'separator'; id: string }
  | { type: 'label'; id: string; label: string }
  | { type: 'submenu'; id: string; label: string; icon?: LucideIcon; items: MenuEntry[] };

export interface DropdownMenuProps {
  /** The trigger element (Button, IconButton). */
  trigger: ReactNode;
  items: MenuEntry[];
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Minimum width in px. Default 180. */
  minWidth?: number;
}

const ITEM =
  'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-nav text-ink outline-none transition-colors data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-card';
const CONTENT = 'z-[95] rounded-sm border border-line bg-panel p-1';

function renderEntries(items: MenuEntry[]): ReactNode {
  return items.map((entry) => {
    switch (entry.type) {
      case 'separator':
        return <Menu.Separator key={entry.id} className="my-1 h-px bg-hair" />;
      case 'label':
        return (
          <Menu.Label key={entry.id} className="label-mono px-2 pb-1 pt-1.5 text-mute">
            {entry.label}
          </Menu.Label>
        );
      case 'checkbox':
        return (
          <Menu.CheckboxItem key={entry.id} checked={entry.checked} onCheckedChange={entry.onCheckedChange} disabled={entry.disabled} className={cn(ITEM, 'pl-6')}>
            <Menu.ItemIndicator className="absolute left-1.5 text-brand-text">
              <Check size={11} aria-hidden />
            </Menu.ItemIndicator>
            {entry.label}
          </Menu.CheckboxItem>
        );
      case 'submenu':
        return (
          <Menu.Sub key={entry.id}>
            <Menu.SubTrigger className={cn(ITEM, 'data-[state=open]:bg-card')}>
              {entry.icon ? <entry.icon size={12} strokeWidth={1.5} aria-hidden className="text-mute" /> : null}
              <span className="flex-1">{entry.label}</span>
              <ChevronRight size={11} aria-hidden className="text-mute" />
            </Menu.SubTrigger>
            <Menu.Portal>
              <Menu.SubContent sideOffset={4} alignOffset={-4} className={cn(CONTENT, 'min-w-[160px]')}>
                {renderEntries(entry.items)}
              </Menu.SubContent>
            </Menu.Portal>
          </Menu.Sub>
        );
      default:
        return (
          <Menu.Item key={entry.id} disabled={entry.disabled} onSelect={entry.onSelect} className={cn(ITEM, entry.destructive ? 'text-brand-text data-[highlighted]:text-brand-hi' : '')}>
            {entry.icon ? <entry.icon size={12} strokeWidth={1.5} aria-hidden className={entry.destructive ? '' : 'text-mute'} /> : null}
            <span className="flex-1">{entry.label}</span>
            {entry.shortcut ? <span className="font-mono text-2xs text-mute">{entry.shortcut}</span> : null}
          </Menu.Item>
        );
    }
  });
}

/** Radix DropdownMenu: hairline panel, --card highlight, mono shortcuts, keyboard operable. */
export function DropdownMenu({ trigger, items, align = 'end', side = 'bottom', open, onOpenChange, minWidth = 180 }: DropdownMenuProps) {
  return (
    <Menu.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align={align} side={side} sideOffset={4} collisionPadding={8} style={{ minWidth }} className={CONTENT}>
          {renderEntries(items)}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
